import {
  call,
  takeLatest,
  takeEvery,
  delay,
  select,
  put,
  race,
  take,
  debounce,
} from 'redux-saga/effects';

import {
  getEventTypes,
  uploadEventsFile,
  getUserEvents as getUserEventsApi,
  getEvents as getEventsApi,
  deleteUserEvent as deleteUserEventApi,
  bulkOverwriteEventSeries as bulkOverwriteEventSeriesAPI,
  excludeSeriesEvent as excludeSeriesEventApi,
} from '../../services';
import {
  displayNotification,
  removeNotification,
  getTemporaryId,
  checkOnline,
  getNotification,
} from '../notifications';
import { uploadFileStarted, uploadFileCompleted, clearUploaded, getUploading } from '../files';
import { REFRESH_VALUES } from '../application';
import { doRequestTags, doCreateTag } from '../tags';
import {
  FETCH_USER_EVENT_TYPES,
  UPLOAD_IOT_EVENTS_FILE,
  STOP_FETCH_EXPORT_EVENTS,
  REQUEST_NOTIFY_EVENTS,
  FETCH_SERIES_EVENTS,
  DELETE_USER_EVENT,
  EXCLUDE_USER_EVENT_OCCURRENCE,
  FETCH_EXPORT_EVENTS,
  BULK_OVERWRITE_SERIES,
  receiveUserEventTypes,
  iotEventsAdded,
  receiveNotifyEvents,
  receiveSeriesEvents,
  exportingDone,
  setImportedAt,
  setImportErrors,
} from './actions';
import { getEventNotifications } from './selectors';

export function* doGetUserEventTypes() {
  // TO DO: site specific event types?
  try {
    const { values: userEventTypes } = yield call(getEventTypes);
    yield put(
      receiveUserEventTypes(userEventTypes.toSorted(({ id: a }, { id: b }) => a.localeCompare(b)))
    );
  } catch (error) {
    console.error('Unable to fetch user event types:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getEventTypes', 'error')()));
  }
}

export function* doUploadEventsFile(action) {
  const { file } = action;
  const formData = new FormData();
  for (const key of Object.keys(file)) {
    if (Array.isArray(file[key])) {
      for (const item of file[key]) {
        formData.append(`${key}[]`, item);
      }
    } else {
      formData.append(key, file[key]);
    }
  }

  yield put(uploadFileStarted(file));
  const temporaryId = getTemporaryId();
  yield put(displayNotification(getNotification('injectEvents', 'pre')(file.file.name)));

  try {
    const upload = yield call(uploadEventsFile, formData, file.source);
    yield put(removeNotification(temporaryId));

    const errs = upload.errors;
    if (errs.length > 0) {
      console.error(errs);
      yield put(uploadFileCompleted(file, 'ERROR'));
      yield put(
        displayNotification(
          getNotification('injectEvents', 'partialSuccess')(upload.eventsAdded, errs)
        )
      );
    } else {
      yield put(uploadFileCompleted(file, 'SUCCESS'));
      yield put(
        displayNotification(getNotification('injectEvents', 'success')(upload.eventsAdded))
      );
    }
    yield put(iotEventsAdded(Date.now()));
  } catch (error) {
    console.error('Unable to inject IoT events:', error);
    yield call(checkOnline);
    yield put(uploadFileCompleted(file, 'ERROR'));
    yield put(displayNotification(getNotification('injectEvents', 'error')(file.file.name)));
  }
  yield delay(3000);
  const numberUploads = yield select(getUploading);
  if (numberUploads && numberUploads.length === 0) {
    yield put(clearUploaded());
  }
}

export function* doGetNotifyEvents(action) {
  const { siteId } = action;
  try {
    const eventNotifications = yield select(getEventNotifications);
    const { data: events } = yield call(getUserEventsApi, {
      site: siteId,
      state: ['overdue', 'todo'],
      order: 'asc',
    });

    const newEvents = events.filter(
      (event) => !(eventNotifications.notifiedEvents || []).includes(event.id)
    );

    yield put(
      receiveNotifyEvents({
        overdueCount: newEvents.filter((event) => event.state === 'overdue').length,
        todoCount: newEvents.filter((event) => event.state === 'todo').length,
        notifiedEvents: events.map((event) => event.id),
      })
    );
  } catch (error) {
    yield console.error('Unable to fetch user events:', error);
    yield call(checkOnline);
  }
}

export function* doGetSeriesEvents(action) {
  const { query, callback } = action;
  try {
    const { data: events = [] } = yield call(getUserEventsApi, query);
    yield put(receiveSeriesEvents(events));
    if (callback && typeof callback === 'function') {
      callback();
    }
  } catch (error) {
    yield console.error('Unable to fetch series events:', error);
    yield call(checkOnline);
  }
}

const requestEvents = async (query, perRequestCallback, iteration = 0) => {
  const { values, total, sourceOptions } = await getEventsApi(query);
  perRequestCallback(iteration + 1);
  // if we have 10k events we can assume we need to make another req as we've hit the limit
  if (values.length === 10_000) {
    const { values: nextValues, total: nextTotal } = await requestEvents(
      { ...query, sourceOptions },
      perRequestCallback,
      iteration + 1
    );
    return { values: [...values, ...nextValues], total: total + nextTotal };
  }
  return { values, total };
};

export function* doFetchExportEvents(action) {
  // fetch up to 300k events for export
  const { eventType, query, exportCallback, perRequestCallback } = action;
  try {
    if (eventType === 'iot') {
      // fetch, recursively until all events
      const { values: allEvents } = yield call(requestEvents, query, perRequestCallback);
      yield call(exportCallback, allEvents);
    }
    if (eventType === 'user') {
      const { data: allEvents, eventsStats: stats } = yield call(getUserEventsApi, {
        ...query,
        withRecurring: true,
      });
      yield call(exportCallback, query.statistics ? stats : allEvents);
    }
    yield put(exportingDone());
  } catch (error) {
    yield console.error('Unable to export user events:', error);
    yield call(exportCallback);
    yield put(displayNotification(getNotification('exportEvents', 'error')()));
    yield call(checkOnline);
  }
}

export function* doDeleteUserEvent(action) {
  const { eventId, callback } = action;
  try {
    yield call(deleteUserEventApi, eventId);
    yield put(displayNotification(getNotification('deleteEvent', 'success')(eventId)));
    if (callback && typeof callback === 'function') {
      callback();
    }
  } catch (error) {
    console.error('Unable to delete event:', error);
    yield put(displayNotification(getNotification('deleteEvent', 'error')(eventId)));
    yield call(checkOnline);
  }
}

export function* doBulkOverwriteEventSeries(action) {
  const { data } = action;

  try {
    const tags = yield select((state) => state.tags.tags);
    let tag = tags.find((t) => t.name === data.overwriteTag);
    if (!tag) {
      // Create tag if it does not exist
      yield call(doCreateTag, {
        tag: {
          org: data.org,
          site: data.site,
          name: data.overwriteTag,
          categories: ['events'],
        },
      });

      yield call(doRequestTags, { query: { site: data.site } });
      const updatedTags = yield select((state) => state.tags.tags);
      tag = updatedTags.find((t) => t.name === data.overwriteTag);
    }

    if (!tag) {
      // This should never happen
      throw new Error('No tag found');
    }

    // Put event series in file
    const jsonBlob = new Blob([JSON.stringify(data.eventSeries)], { type: 'application/json' });
    const fd = new FormData();
    fd.append('file', jsonBlob, 'data.json');
    fd.append('site', data.site);
    fd.append('org', data.org);
    fd.append('overwriteTag', tag.id);

    yield bulkOverwriteEventSeriesAPI(fd);

    yield put(setImportedAt(Date.now()));
    yield put(displayNotification(getNotification('overwriteEventSeries', 'success')()));
  } catch (error) {
    console.error('Error occurred while creating event series:', error);
    yield put(setImportErrors(['An error occured when creating event series']));
    yield put(displayNotification(getNotification('overwriteEventSeries', 'error')()));
    yield call(checkOnline);
  }
}

export function* doExcludeUserEventOccurrence(action) {
  const { event, callback } = action;
  try {
    yield call(excludeSeriesEventApi, event);
    yield put(displayNotification(getNotification('deleteEvent', 'success')(event.id)));
    if (callback && typeof callback === 'function') {
      callback();
    }
  } catch (error) {
    console.error('Unable to delete event:', error);
    yield put(displayNotification(getNotification('deleteEvent', 'error')(event.id)));
    yield call(checkOnline);
  }
}

export function* doStartFetchExportEvents(action) {
  yield race([call(doFetchExportEvents, action), take(STOP_FETCH_EXPORT_EVENTS)]);
}

export const sagas = [
  takeLatest(FETCH_USER_EVENT_TYPES, doGetUserEventTypes),
  takeEvery(UPLOAD_IOT_EVENTS_FILE, doUploadEventsFile),
  takeLatest(REQUEST_NOTIFY_EVENTS, doGetNotifyEvents),
  takeLatest(FETCH_SERIES_EVENTS, doGetSeriesEvents),
  takeLatest(DELETE_USER_EVENT, doDeleteUserEvent),
  takeLatest(EXCLUDE_USER_EVENT_OCCURRENCE, doExcludeUserEventOccurrence),
  takeLatest(FETCH_EXPORT_EVENTS, doStartFetchExportEvents),
  takeLatest(BULK_OVERWRITE_SERIES, doBulkOverwriteEventSeries),
  debounce(500, REFRESH_VALUES, doGetNotifyEvents),
];
